import React from "react";
import { Pencil, Clock } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { SECTION_LABEL, fmtDateTime } from "./constants";

const ORDER = ["group", "rr", "main", "upper", "lower", "playoffs", "final", "third"];

export function TeamLogo({ team, logos, size = "w-6 h-6" }) {
  const src = logos?.[team];
  if (src) return <img src={mediaUrl(src)} alt="" className={`${size} rounded-full object-cover shrink-0`} />;
  return <span className={`${size} rounded-full bg-white/10 grid place-items-center ${size.includes("text-") ? "" : "text-[10px]"} font-bold text-white/70 shrink-0`}>{team === "TBD" || team === "BYE" ? "–" : (team || "?")[0]}</span>;
}

export function MatchCard({ m, logos, onEdit }) {
  const live = m.status === "live";
  return (
    <div className={`relative card-surface rounded-lg overflow-hidden w-[220px] ${live ? "ring-1 ring-[hsl(var(--accent))]" : ""}`} data-testid={`match-${m.id}`}>
      {[["a", "sa"], ["b", "sb"]].map(([t, s]) => {
        const won = m.winner && m.winner === m[t];
        return (
          <div key={t} className={`flex items-center gap-2 px-3 py-2 text-sm ${won ? "bg-white/[0.06] text-white font-semibold" : m[t] === "BYE" || m[t] === "TBD" ? "text-white/30" : "text-white/70"}`}>
            <TeamLogo team={m[t]} logos={logos} size="w-5 h-5" />
            <span className="truncate flex-1">{m[t]}</span>
            <span className={`font-head ${won ? "text-[hsl(var(--primary))]" : ""}`}>{m.status === "upcoming" && !m.winner ? "" : m[s]}</span>
          </div>
        );
      })}
      <div className="flex items-center justify-between px-3 py-1 text-[10px] text-white/40 border-t border-white/5">
        <span className="flex items-center gap-1">{live ? <span className="text-[hsl(var(--accent))] font-bold live-dot">● LIVE</span> : <><Clock size={10} /> {m.scheduled_at ? fmtDateTime(m.scheduled_at) : "TBD"}</>}</span>
        {onEdit && m.a !== "BYE" && m.b !== "BYE" && (
          <button onClick={() => onEdit(m)} className="flex items-center gap-1 text-white/60 hover:text-white" data-testid={`edit-match-${m.id}`}><Pencil size={10} /> Edit</button>
        )}
      </div>
    </div>
  );
}

function StandingsTable({ rows, title, advance }) {
  return (
    <div className="card-surface rounded-xl overflow-hidden min-w-[300px]" data-testid={`standings-${title}`}>
      <div className="px-4 py-2.5 text-sm font-bold text-white border-b border-white/10">{title}</div>
      <table className="w-full text-xs">
        <thead className="text-white/40"><tr>{["#", "Team", "P", "W", "D", "L", "+/-", "Pts"].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.team} className={`border-t border-white/5 ${advance && i < advance ? "text-white" : "text-white/60"}`}>
              <td className="px-2 py-1.5">{i + 1}</td><td className="px-2 py-1.5 font-semibold truncate max-w-[120px]">{r.team}</td>
              <td className="px-2">{r.played}</td><td className="px-2">{r.w}</td><td className="px-2">{r.d}</td><td className="px-2">{r.l}</td><td className="px-2">{r.diff}</td><td className="px-2 font-bold">{r.pts}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Rounds({ matches, logos, onEdit, tree }) {
  const rounds = [...new Set(matches.map((m) => m.round))].sort((a, b) => a - b);
  return (
    <div className="overflow-x-auto pb-3">
      <div className="flex gap-10 min-w-max">
        {rounds.map((r) => {
          const ms = matches.filter((m) => m.round === r).sort((a, b) => a.order - b.order);
          return (
            <div key={r} className={`flex flex-col gap-3 ${tree ? "bracket-round" : ""}`}>
              <div className="eyebrow text-white/40">{ms[0]?.round_name}</div>
              <div className="flex flex-col justify-around gap-3 flex-1">{ms.map((m) => <div key={m.id} className={tree ? "bracket-node relative" : ""}><MatchCard m={m} logos={logos} onEdit={onEdit} /></div>)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BracketView({ tournament, onEdit }) {
  const matches = tournament.bracket?.matches || [];
  const logos = Object.fromEntries((tournament.participants || []).map((p) => [p.name, p.logo]));
  if (!matches.length) return <div className="text-white/40 py-12 text-center" data-testid="bracket-empty">The bracket has not been generated yet.</div>;
  const sections = ORDER.filter((s) => matches.some((m) => m.section === s));
  const st = tournament.standings || {};
  return (
    <div className="space-y-8" data-testid="bracket-view">
      {sections.map((s) => {
        const ms = matches.filter((m) => m.section === s);
        if (s === "group") {
          const groups = [...new Set(ms.map((m) => m.group))].sort();
          return (
            <section key={s} className="space-y-6">
              <h3 className="text-base font-bold text-white">{SECTION_LABEL[s]}</h3>
              {groups.map((g) => (
                <div key={g} className="space-y-3">
                  <StandingsTable title={`Group ${g}`} rows={st[`group:${g}`] || []} advance={tournament.advance_per_group} />
                  <Rounds matches={ms.filter((m) => m.group === g)} logos={logos} onEdit={onEdit} />
                </div>
              ))}
            </section>
          );
        }
        return (
          <section key={s} className="space-y-3">
            <h3 className="text-base font-bold text-white">{SECTION_LABEL[s]}</h3>
            {s === "rr" && <StandingsTable title="Standings" rows={st.rr || []} />}
            <Rounds matches={ms} logos={logos} onEdit={onEdit} tree={!["rr", "third", "final"].includes(s)} />
          </section>
        );
      })}
    </div>
  );
}
